/**
 * Secret API Service
 *
 * API methods for secret operations including creation, updates, and access grants.
 * Updated to use standardized API response format.
 *
 * SECURITY NOTES:
 * - Secret values are NEVER returned via API (write-only from UI)
 * - Secret values are encrypted via tenant KMS key on backend
 * - Only applications with grants can access secret values at runtime
 */

import apiClient from "./client";
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from "../types/api.types";
import type {
  SecretSanitized,
  SecretCreateDTO,
  SecretUpdateDTO,
  SecretListQuery,
} from "../types/secret.types";

// ============================================================================
// SECRET SERVICE
// ============================================================================

export const secretService = {
  /**
   * List all secrets for a tenant
   */
  listSecrets: async (tenantId: string, query?: SecretListQuery): Promise<SecretSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<SecretSanitized>>(`/tenants/${tenantId}/secrets`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Get secret by ID (without value - values are never returned)
   */
  getSecret: async (tenantId: string, secretId: string): Promise<SecretSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<SecretSanitized>>(`/tenants/${tenantId}/secrets/${secretId}`);
    return response.data.data;
  },

  /**
   * Create a new secret
   */
  createSecret: async (tenantId: string, data: SecretCreateDTO): Promise<SecretSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<SecretSanitized>>(`/tenants/${tenantId}/secrets`, data);
    return response.data.data;
  },

  /**
   * Update secret (name, description, or value)
   * Note: If value is provided, it will be encrypted on backend
   */
  updateSecret: async (tenantId: string, secretId: string, data: SecretUpdateDTO): Promise<SecretSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<SecretSanitized>>(`/tenants/${tenantId}/secrets/${secretId}`, data);
    return response.data.data;
  },

  /**
   * Delete secret
   */
  deleteSecret: async (tenantId: string, secretId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(`/tenants/${tenantId}/secrets/${secretId}`);
    return { message: response.data.message };
  },
};

export default secretService;
